import { styled } from "goober";
import { type RefObject, useLayoutEffect } from "react";

export const ChartMarker = styled("div")`
  position: absolute;
  bottom: -20px;
  top: 0;
  width: 2px;
  transform: translateX(-50%);
  background: var(--gray-400);
  pointer-events: none;
`;

// The rounded time/date label that rides under the marker at the hovered (or
// "now") position. Consumers set `left` and, when highlighting a hover, flip
// the background to var(--text-primary) with var(--text-invert) text.
export const ChartPill = styled("div")`
  position: absolute;
  bottom: -44px;
  transform: translateX(-50%);
  color: var(--text-primary);
  background: var(--alpha8);
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  letter-spacing: -1%;
  border-radius: 999px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 1;
`;

// ChartTooltip is the only tooltip in the app that flips per-mode (uses
// var(--text-primary) as bg, so light bg in dark mode and vice versa). The
// secondary header text follows --text-secondary so it auto-inverts with the
// surrounding surface, rather than --tooltip-fg-muted which is pinned
// cream-alpha and would vanish on the light-bg dark-mode variant.
export const ChartTooltip = styled("div")`
  position: absolute;
  top: 0;
  background: var(--text-primary);
  color: var(--text-invert);
  border-radius: 8px;
  padding: 8px 10px;
  pointer-events: none;
  z-index: 1;
  width: 120px;
  font-size: 10px;
  line-height: 14px;
`;

export const ChartTooltipHeader = styled("div")`
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-secondary);
  margin-bottom: 4px;
`;

export const ChartTooltipRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ChartTooltipDot = styled("span")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const ChartTooltipCount = styled("span")`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
`;

// Nudge an absolutely-positioned tooltip back inside its wrapper when it would
// overflow the right edge. The tooltip always sits to the right of its anchor,
// so only the right edge can clip.
export function useClampRight<E extends HTMLElement, W extends HTMLElement>(
  ref: RefObject<E | null>,
  wrapperRef: RefObject<W | null>,
  dep: unknown
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    const wrapper = wrapperRef.current;
    if (!el || !wrapper) return;
    const elRect = el.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    if (elRect.right > wrapperRect.right) {
      el.style.left = "auto";
      el.style.right = "8px";
    } else {
      el.style.right = "";
    }
  }, [ref, wrapperRef, dep]);
}
